import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { unenrollFromCourse, unenrollFromCurriculum } from "@/lib/unenrollRpc";
import { toast } from "sonner";
import { UserMinus, Users } from "lucide-react";
import { getStorageUrl } from "@/lib/storage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EnrolledUser {
  id: string;
  user_id: string;
  enrolled_at: string;
  profile: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  } | null;
}

interface EnrolledUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "course" | "curriculum";
  id: string;
  title: string;
}

export function EnrolledUsersDialog({
  open,
  onOpenChange,
  type,
  id,
  title,
}: EnrolledUsersDialogProps) {
  const queryClient = useQueryClient();
  const [unenrollingUser, setUnenrollingUser] = useState<EnrolledUser | null>(null);

  const { data: enrolledUsers, isLoading } = useQuery({
    queryKey: ["enrolled-users", type, id],
    queryFn: async () => {
      if (type === "course") {
        const { data, error } = await supabase
          .from("course_enrollments")
          .select(`
            id,
            user_id,
            enrolled_at,
            profile:profiles!course_enrollments_user_id_fkey(id, full_name, avatar_url)
          `)
          .eq("course_id", id)
          .order("enrolled_at", { ascending: false });

        if (error) throw error;
        return data as EnrolledUser[];
      } else {
        const { data, error } = await supabase
          .from("curriculum_enrollments")
          .select(`
            id,
            user_id,
            enrolled_at,
            profile:profiles!curriculum_enrollments_user_id_fkey(id, full_name, avatar_url)
          `)
          .eq("curriculum_id", id)
          .order("enrolled_at", { ascending: false });

        if (error) throw error;
        return data as EnrolledUser[];
      }
    },
    enabled: open && !!id,
  });

  const unenrollMutation = useMutation({
    mutationFn: async (enrollment: EnrolledUser) => {
      // Atomic server-side unenroll for BOTH branches. Previously each branch
      // issued direct DELETEs; course_enrollments had no DELETE policy at all
      // and curriculum_enrollments had no ADMIN delete policy, so an admin
      // unenrolling someone else silently no-opped (200, 0 rows, error === null)
      // while the UI reported success. The RPCs run the whole cascade in one
      // transaction and raise if nothing was deleted.
      if (type === "course") {
        await unenrollFromCourse(id, enrollment.user_id);
      } else {
        await unenrollFromCurriculum(id, enrollment.user_id);
      }
    },
    onSuccess: () => {
      toast.success("User unenrolled successfully");
      queryClient.invalidateQueries({ queryKey: ["enrolled-users", type, id] });
      queryClient.invalidateQueries({ queryKey: ["instructor-courses"] });
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
      setUnenrollingUser(null);
    },
    onError: (error) => {
      toast.error("Failed to unenroll user");
      console.error("Unenroll error:", error);
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Enrolled Users - {title}
            </DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : enrolledUsers && enrolledUsers.length > 0 ? (
            <ScrollArea className="max-h-96">
              <div className="space-y-2">
                {enrolledUsers.map((enrollment) => (
                  <div
                    key={enrollment.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={getStorageUrl("avatars", enrollment.profile?.avatar_url) || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {enrollment.profile?.full_name?.charAt(0) || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">
                          {enrollment.profile?.full_name || "Unknown User"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setUnenrollingUser(enrollment)}
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No users enrolled yet</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!unenrollingUser} onOpenChange={() => setUnenrollingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unenroll User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {unenrollingUser?.profile?.full_name || "this user"} from{" "}
              {type === "curriculum"
                ? "this curriculum and all its courses"
                : "this course"}
              . Their progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => unenrollingUser && unenrollMutation.mutate(unenrollingUser)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={unenrollMutation.isPending}
            >
              {unenrollMutation.isPending ? "Unenrolling..." : "Unenroll"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
